import { buildCalendarTimeSlots, MIN_SLOT_INTERVAL_MINUTES } from "@/lib/calendarGranularity";

const DEFAULT_START_HOUR = 7;
const DEFAULT_END_HOUR = 20;

export function buildTimeChipSlots({
  startHour = DEFAULT_START_HOUR,
  endHour = DEFAULT_END_HOUR,
  slotIntervalMinutes = MIN_SLOT_INTERVAL_MINUTES,
} = {}) {
  return buildCalendarTimeSlots({ startHour, endHour, slotIntervalMinutes });
}

export function formatTimeChip(t) {
  if (!t) return "";
  const [hStr, mStr] = String(t).split(":");
  const h = Number(hStr);
  const m = Number(mStr || 0);
  if (Number.isNaN(h) || Number.isNaN(m)) return String(t);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function normalizeSlot(slot) {
  if (typeof slot === "string") {
    return { time: slot, available: true, unavailable_reason: null };
  }
  const time = slot?.time || slot?.slot_time || slot?.value || "";
  return {
    time: String(time).slice(0, 5),
    available: slot?.available !== false,
    unavailable_reason: slot?.unavailable_reason || slot?.reason || null,
  };
}

export default function TimeChipGrid({
  value,
  onChange,
  slotIntervalMinutes = MIN_SLOT_INTERVAL_MINUTES,
  slots = buildTimeChipSlots({ slotIntervalMinutes }),
  testIdPrefix = "time-chip",
  maxHeightClass = "max-h-56",
}) {
  const normalized = slots.map(normalizeSlot).filter((slot) => slot.time);
  const byHour = normalized.reduce((acc, slot) => {
    const h = slot.time.slice(0, 2);
    acc[h] = acc[h] || [];
    acc[h].push(slot);
    return acc;
  }, {});
  const hours = Object.keys(byHour).sort();

  return (
    <div className={`mt-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 ${maxHeightClass}`}>
      <div className="space-y-1.5">
        {hours.map((h) => (
          <div key={h} className="grid grid-cols-[40px_1fr] items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {formatTimeChip(`${h}:00`).replace(":00", "").trim()}
            </span>
            <div className="flex flex-wrap gap-1">
              {byHour[h].map((slot) => {
                const active = value === slot.time;
                const disabled = !slot.available;
                return (
                  <button
                    key={slot.time}
                    type="button"
                    onClick={() => {
                      if (!disabled) onChange(slot.time);
                    }}
                    disabled={disabled}
                    data-testid={`${testIdPrefix}-${slot.time}`}
                    title={disabled ? (slot.unavailable_reason || "Unavailable") : formatTimeChip(slot.time)}
                    className={`min-h-7 rounded-md px-2 text-[11px] font-semibold transition ${
                      active
                        ? "bg-teal-600 text-white shadow-sm"
                        : disabled
                          ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400"
                          : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    <span>{slot.time.slice(3) === "00" ? formatTimeChip(slot.time) : `:${slot.time.slice(3)}`}</span>
                    {disabled && slot.unavailable_reason ? (
                      <span className="ml-1 text-[9px] font-bold uppercase tracking-wide">{slot.unavailable_reason}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
